import React, {useCallback, useEffect, useState} from "react";
import {APanel, APanelBody} from "@cisco-sbg-ui/magna-react";
import {useDispatch, useSelector} from "react-redux";
import {useParams} from "react-router-dom";
import {EntityDrawer} from "features/drawers";
import IncidentDetectionDrawer from "features/drawers/IncidentDetectionDrawer/IncidentDetectionDrawer";
import EventsTable from "features/EventsTable/EventsTable";
import EventsTableToolbar from "features/EventsTable/toolbar/EventsTableToolbar";
import useDrawer from "hooks/useDrawer";
import useOverflowDrawer from "hooks/useOverflowDrawer";
import {getEvents} from "store/features/incidentDetectionSlice";
import {
  selectAllEvents,
  selectEventsLoading
} from "store/selectors/incidentDetectionSelectors";
import {ALL_OPTION, EVENT_TYPE_OPTIONS_MAP, SEVERITY_MAP} from "models/options";

const IncidentDetailDetection = () => {
  const {id} = useParams();

  const dispatch = useDispatch();
  const allEvents = useSelector(selectAllEvents);
  const isLoading = useSelector(selectEventsLoading);

  const [showImportantOnly, setShowImportantOnly] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedSources, setSelectedSources] = useState([]);
  const [selectedSeverity, setSelectedSeverity] = useState(null);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [entityDrawerDataType, setEntityDrawerDataType] = useState();

  /** Main Incident Detection Drawer */
  const {
    overflowContent,
    addOverflowContent,
    removeOverflowContent,
    overflowLayersCount,
    isOpen: isDetectionDrawerOpen,
    open: openDetectionDrawer,
    close: closeDetectionDrawer,
    data: selectedEvent
  } = useOverflowDrawer();

  /** EntityDrawer */
  const {
    isOpen: isEntityDrawerOpen,
    open: openEntityDrawer,
    close: closeEntityDrawer,
    data: entityDrawerData
  } = useDrawer();

  /**
   * This instance of Entity Drawer may render any EntityType
   * depending which dataset is passed 'onOpen'.
   * For that reason, also track the `entityDrawerDataType` selected
   * so that the EntityDrawer component knows the data type to render.
   *
   * `entityDrawerDataType` should always be set in the same action as opening
   * the drawer with a new dataset.
   */
  const handleOpenEntityDrawer = useCallback(
    (data, entityType) => {
      const doOpen = () =>
        openEntityDrawer(data).then(() => setEntityDrawerDataType(entityType));
      isDetectionDrawerOpen ? closeDetectionDrawer().then(doOpen) : doOpen();
    },
    [closeDetectionDrawer, isDetectionDrawerOpen, openEntityDrawer]
  );

  const handleOpenDetectionDrawer = useCallback(
    (data) => {
      const doOpen = () => openDetectionDrawer(data);
      isEntityDrawerOpen ? closeEntityDrawer().then(doOpen) : doOpen();
    },
    [closeEntityDrawer, isEntityDrawerOpen, openDetectionDrawer]
  );

  const hasFilter = (condition) => (condition ? 1 : 0);

  const filterCount =
    hasFilter(showImportantOnly) +
    hasFilter(selectedType !== null) +
    hasFilter(selectedSeverity !== null) +
    selectedSources.length;

  useEffect(() => {
    if (allEvents.length <= 0) {
      dispatch(getEvents({id}));
    }
  }, [dispatch, id, allEvents.length]);

  useEffect(() => {
    setFilteredEvents(
      allEvents.filter(
        (event) =>
          isImportant(event) &&
          hasEventType(event) &&
          hasSeverity(event) &&
          hasSource(event)
      )
    );
  }, [
    allEvents,
    showImportantOnly,
    selectedType,
    selectedSources,
    selectedSeverity
  ]);

  const isImportant = ({notable}) =>
    !showImportantOnly || (showImportantOnly && notable);

  const hasEventType = ({investigationId, original}) => {
    if (
      !selectedType ||
      selectedType.value === EVENT_TYPE_OPTIONS_MAP.all.value
    ) {
      return true; // No Selection or "All" selected
    } else if (
      selectedType.value === EVENT_TYPE_OPTIONS_MAP.original.value &&
      original &&
      !investigationId
    ) {
      return true; // Is an Original event
    } else if (
      selectedType.value === EVENT_TYPE_OPTIONS_MAP.investigated.value &&
      Boolean(investigationId)
    ) {
      return true; // Is an Investigated event.
    }
    return false; // No match, filter out by default.
  };

  const hasSeverity = ({severity}) => {
    return (
      !selectedSeverity ||
      selectedSeverity.value === ALL_OPTION.value ||
      (!severity && selectedSeverity.value === SEVERITY_MAP.None.value) ||
      severity === selectedSeverity.value
    );
  };

  const hasSource = (event) =>
    selectedSources?.length <= 0 || selectedSources.includes(event.source);

  return (
    <>
      <APanel
        className={"mx-0"}
        data-testid={"IncidentDetailDetection-Container"}>
        <APanelBody>
          <EventsTableToolbar
            importantOnly={showImportantOnly}
            setImportantOnly={setShowImportantOnly}
            selectedSources={selectedSources}
            setSelectedType={setSelectedType}
            setSelectedSources={setSelectedSources}
            setSelectedSeverity={setSelectedSeverity}
            className="mb-2"
          />
          <EventsTable
            tableData={filteredEvents}
            eventTotalCount={filteredEvents.length}
            openDetectionDrawer={handleOpenDetectionDrawer}
            openEntityDrawer={handleOpenEntityDrawer}
            {...{
              filterCount,
              isLoading
            }}
          />
        </APanelBody>
      </APanel>
      <IncidentDetectionDrawer
        overflowLayersCount={overflowLayersCount}
        overflowContent={overflowContent}
        event={selectedEvent}
        addOverflowContent={addOverflowContent}
        removeOverflowContent={removeOverflowContent}
        isOpen={isDetectionDrawerOpen}
        onClose={closeDetectionDrawer}
        openEntityDrawer={handleOpenEntityDrawer}
      />
      <EntityDrawer
        count={entityDrawerData?.length || 0}
        data={entityDrawerData}
        entityType={entityDrawerDataType}
        isOpen={isEntityDrawerOpen}
        onClose={closeEntityDrawer}
        entityListProps={{
          showEventsCount: false,
          tester: 5
        }}
      />
    </>
  );
};

export default IncidentDetailDetection;
